import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import OnlineFilterGroup from '@/components/navigation/online-filter-group';
import TgSvg from '@/components/tg-svg';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import PhoneCountryExclusionField from '@/components/clients/phone-country-exclusion-field';
import { Switch } from '@/components/ui/switch';
import type { OnlineStatusFilter } from '@/entities/clients-filter.entity';
import { $myClients, $myClientsSearchValue, $setMyClientsSearchResult, $setMyClientsSearchValue } from '@/entities/my-clients-entity';
import { $myFilter, $updateMyFilter, type IMyFilter } from '@/entities/my-filter-entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { phoneDigitsForSearch } from '@/lib/format-phone';
import { useUnit } from 'effector-react';
import { useEffect, useMemo } from 'react';

function countActiveFilters(filter: IMyFilter) {
	let count = 0;
	if (filter.removeWithoutPhone) count++;
	if (filter.excludePhoneCountries.length > 0) count++;
	if (filter.removeTgUsed) count++;
	if (filter.removeWithoutTgCode) count++;
	if (filter.removeWithTg2FA) count++;
	if (filter.removeTaken) count++;
	return count;
}

export default function ClientsHeader() {
	const [filter, updateFilter, setMyClientsSearchResult, setSearchValue, searchValue, myClients] = useUnit([
		$myFilter,
		$updateMyFilter,
		$setMyClientsSearchResult,
		$setMyClientsSearchValue,
		$myClientsSearchValue,
		$myClients,
	]);

	const activeFilters = countActiveFilters(filter);

	const onlineCount = useMemo(() => myClients.filter(c => c.isOnline).length, [myClients]);

	useEffect(() => {
		if (isDevMode || !socket) return;
		socket.emit(SOCKET_EVENTS.getMyClients, { time: filter.time });
	}, [filter.time]);

	useEffect(() => {
		if (!searchValue.length) {
			setMyClientsSearchResult([]);
			return;
		}

		if (isDevMode) {
			const q = searchValue.toLowerCase();
			const qDigits = phoneDigitsForSearch(searchValue);
			setMyClientsSearchResult(
				myClients.filter(
					c =>
						c.id.toLowerCase().includes(q) ||
						(c.phone && (c.phone.toLowerCase().includes(q) || phoneDigitsForSearch(c.phone).includes(qDigits))) ||
						c.comment?.toLowerCase().includes(q) ||
						c.phones.some(p => p.number && (p.number.toLowerCase().includes(q) || phoneDigitsForSearch(p.number).includes(qDigits))),
				),
			);
			return;
		}

		if (!socket) return;
		const timeout = setTimeout(() => {
			socket.emit(SOCKET_EVENTS.myClientsSearch, { search: searchValue });
		}, 400);

		return () => {
			clearTimeout(timeout);
		};
	}, [searchValue, setMyClientsSearchResult, myClients]);

	return (
		<div className='flex flex-col gap-3'>
			<Input placeholder='Поиск' value={searchValue} onChange={e => setSearchValue(e.target.value)} />
			<OnlineFilterGroup
				value={filter.onlineStatus}
				onChange={(v: OnlineStatusFilter) => updateFilter({ onlineStatus: v })}
				onlineCount={onlineCount}
			/>
			<div className='flex gap-3 justify-center'>
				<Select disabled={!!searchValue.length} value={filter.time} onValueChange={v => updateFilter({ time: v })}>
					<SelectTrigger className='w-[180px]'>
						<SelectValue placeholder='Логи по времени' />
					</SelectTrigger>
					<SelectContent defaultValue={filter.time}>
						<SelectGroup>
							<SelectLabel>Время</SelectLabel>
							<SelectItem value='15m'>Логи за 15 мин.</SelectItem>
							<SelectItem value='1h'>Логи за 1 час</SelectItem>
							<SelectItem value='6h'>Логи за 6 часов</SelectItem>
							<SelectItem value='12h'>Логи за 12 часов</SelectItem>
							<SelectItem value='1d'>Логи за 1 день</SelectItem>
						</SelectGroup>
					</SelectContent>
				</Select>
				<Dialog>
					<form>
						<DialogTrigger asChild>
							<Button disabled={!!searchValue.length} variant='outline' className='relative'>
								Фильтры
								{activeFilters > 0 && (
									<span className='absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-chart-2 text-background text-[11px] font-semibold flex items-center justify-center'>
										{activeFilters}
									</span>
								)}
							</Button>
						</DialogTrigger>
						<DialogContent className='sm:max-w-[440px]'>
							<DialogHeader>
								<DialogTitle>Фильтры</DialogTitle>
							</DialogHeader>
							<div className='grid gap-4'>
								<h2 className='text-muted-foreground font-semibold'>Номера телефонов</h2>
								<div className='flex gap-3'>
									<Switch id='1' onCheckedChange={v => updateFilter({ removeWithoutPhone: v })} checked={filter.removeWithoutPhone} />
									<Label htmlFor='1'>Убрать логи без номеров</Label>
								</div>
								<PhoneCountryExclusionField
									value={filter.excludePhoneCountries}
									onChange={excludePhoneCountries => updateFilter({ excludePhoneCountries })}
								/>
							</div>
							<div className='grid gap-4'>
								<h2 className='text-muted-foreground font-semibold'>Телеграм</h2>
								<div className='flex gap-3'>
									<Switch id='3' onCheckedChange={v => updateFilter({ removeTgUsed: v })} checked={filter.removeTgUsed} />
									<Label htmlFor='3'>
										Убрать использованные <TgSvg size={20} fill='var(--tg-blue)' />
									</Label>
								</div>
								<div className='flex gap-3'>
									<Switch id='4' onCheckedChange={v => updateFilter({ removeWithoutTgCode: v })} checked={filter.removeWithoutTgCode} />
									<Label htmlFor='4'>
										Убрать номера без кода <TgSvg size={20} fill='var(--chart-3)' />{' '}
									</Label>
								</div>
								<div className='flex gap-3'>
									<Switch id='5' onCheckedChange={v => updateFilter({ removeWithTg2FA: v })} checked={filter.removeWithTg2FA} />
									<Label htmlFor='5'>
										Убрать номера с 2FA <TgSvg size={20} fill='var(--chart-5)' />
									</Label>
								</div>
							</div>
							<div className='grid gap-4'>
								<h2 className='text-muted-foreground font-semibold'>Прочее</h2>
								<div className='flex gap-3'>
									<Switch id='6' onCheckedChange={v => updateFilter({ removeTaken: v })} checked={filter.removeTaken} />
									<Label htmlFor='6'>Убрать логи на вбиве</Label>
								</div>
							</div>
							<DialogFooter>
								{activeFilters > 0 && (
									<Button
										variant='outline'
										onClick={() =>
											updateFilter({
												removeWithoutPhone: false,
												excludePhoneCountries: [],
												removeTgUsed: false,
												removeWithoutTgCode: false,
												removeWithTg2FA: false,
												removeTaken: false,
											})
										}
									>
										Сбросить
									</Button>
								)}
								<DialogClose asChild>
									<Button variant='default'>Закрыть</Button>
								</DialogClose>
							</DialogFooter>
						</DialogContent>
					</form>
				</Dialog>
			</div>
		</div>
	);
}
